import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { HelmetProvider } from "react-helmet-async";
import { useEffect, useState, lazy, Suspense } from "react";

// Componente para scroll ao topo quando muda de página
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Componente para redirecionar rotas /site/* para rotas sem /site
const SiteRedirect = ({ basePath }) => {
  const params = useParams();
  const paramValue = params.id || params.slug;
  return <Navigate to={`${basePath}/${paramValue}`} replace />;
};

// Componente de Loading
const PageLoader = () => (
  <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-white dark:from-slate-950 dark:to-slate-900 flex items-center justify-center">
    <div className="glass-panel dark:bg-slate-800/50 rounded-3xl p-8 flex flex-col items-center gap-3">
      <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin"></div>
      <p className="text-slate-600 dark:text-slate-300">Carregando...</p>
    </div>
  </div>
);

// Helper para envolver componente lazy com Suspense
const withSuspense = (Component) => (props) => (
  <Suspense fallback={<PageLoader />}>
    <Component {...props} />
  </Suspense>
);

// Páginas críticas (carregadas imediatamente)
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";

// Site público institucional desativado - apenas CRM ativo

// Lazy loading para páginas do CRM
const FunilVendas = lazy(() => import("@/pages/FunilVendas"));
const Clients = lazy(() => import("@/pages/Clients"));
const Properties = lazy(() => import("@/pages/Properties"));
const Profile = lazy(() => import("@/pages/Profile"));
const ActionLogs = lazy(() => import("@/pages/ActionLogs"));
const Ajuda = lazy(() => import("@/pages/Ajuda"));
const Captacoes = lazy(() => import("@/pages/Captacoes"));
const Empreendimentos = lazy(() => import("@/pages/Empreendimentos"));
const EmpreendimentoDetalhes = lazy(() => import("@/pages/EmpreendimentoDetalhes"));
const ClienteDetalhes = lazy(() => import("@/pages/ClienteDetalhes"));
const CaptacaoDetalhes = lazy(() => import("@/pages/CaptacaoDetalhes"));
const Construtoras = lazy(() => import("@/pages/Construtoras"));
const Proprietarios = lazy(() => import("@/pages/Proprietarios"));
const Agendamentos = lazy(() => import("@/pages/Agendamentos"));
const Equipe = lazy(() => import("@/pages/Equipe"));
const RBACManager = lazy(() => import("@/pages/RBACManager"));
const Disputas = lazy(() => import("@/pages/Disputas"));
const Configuracoes = lazy(() => import("@/pages/Configuracoes"));
const MiniSite = lazy(() => import("@/pages/MiniSite"));
const LandingPages = lazy(() => import("@/pages/LandingPages"));
const LandingPageEditor = lazy(() => import("@/pages/LandingPageEditor"));
const SiteConfig = lazy(() => import("@/pages/SiteConfig"));
const Visitantes = lazy(() => import("@/pages/Visitantes"));
const MetaCAPIConfig = lazy(() => import("@/pages/MetaCAPIConfig"));
const Importacao = lazy(() => import("@/pages/Importacao"));
const Lembretes = lazy(() => import("@/pages/Lembretes"));
const EmailMarketing = lazy(() => import("@/pages/EmailMarketing"));
const Propostas = lazy(() => import("@/pages/Propostas"));
const Atualizacao = lazy(() => import("@/pages/Atualizacao"));
const Tabelas = lazy(() => import("@/pages/Tabelas"));
const UnidadePerfil = lazy(() => import("@/pages/UnidadePerfil"));
const PropostasPage = lazy(() => import("@/pages/Propostas"));
const PropostaPerfil = lazy(() => import("@/pages/PropostaPerfil"));

// Índices Econômicos
const CubHistorico = lazy(() => import("@/pages/CubHistorico"));
const Igpm = lazy(() => import("@/pages/Igpm"));
const Incc = lazy(() => import("@/pages/Incc"));
const Ipca = lazy(() => import("@/pages/Ipca"));

// Bolsão de Leads
const Bolsao = lazy(() => import("@/pages/Bolsao"));
const CampanhasBolsao = lazy(() => import("@/pages/CampanhasBolsao"));
const MinhaCampanha = lazy(() => import("@/pages/MinhaCampanha"));
const CampanhaDetalhes = lazy(() => import("@/pages/CampanhaDetalhes"));

// Pixel Pro - Tracking Comportamental
const PixelProDashboard = lazy(() => import("@/pages/PixelProDashboard"));
const PixelProEvents = lazy(() => import("@/pages/PixelProEvents"));
const PixelProRules = lazy(() => import("@/pages/PixelProRules"));
const PixelProConfig = lazy(() => import("@/pages/PixelProConfig"));
const PixelProWhatsApp = lazy(() => import("@/pages/PixelProWhatsApp"));

// Central ADS - Gestão de Anúncios
const SolkAdsDashboard = lazy(() => import("@/pages/SolkAdsDashboard"));
const CampanhasWhatsApp = lazy(() => import("@/pages/CampanhasWhatsApp"));

// Landing Page WhatsApp (Pública)
const LandingWhatsApp = lazy(() => import("@/pages/LandingWhatsApp"));

// Academy
const Academy = lazy(() => import("@/pages/Academy"));
const AcademyCurso = lazy(() => import("@/pages/AcademyCurso"));
const AcademyAula = lazy(() => import("@/pages/AcademyAula"));
const AcademyAdmin = lazy(() => import("@/pages/AcademyAdmin"));
const AcademyQuestionario = lazy(() => import("@/pages/AcademyQuestionario"));
const SecretariaIA = lazy(() => import("@/pages/SecretariaIA"));

// Compradores
const Compradores = lazy(() => import("@/pages/Compradores"));
const Contratos = lazy(() => import("@/pages/Contratos"));
const Pagamentos = lazy(() => import("@/pages/Pagamentos"));
const Reservas = lazy(() => import("@/pages/Reservas"));
const RedeCorretores = lazy(() => import("@/pages/RedeCorretores"));
const FunilCorretores = lazy(() => import("@/pages/FunilCorretores"));
const CorretorPerfil = lazy(() => import("@/pages/CorretorPerfil"));
const RepasseBancario = lazy(() => import("@/pages/RepasseBancario"));
const CompradorDetalhes = lazy(() => import("@/pages/CompradorDetalhes"));

// Portal do Cliente (público)
const PortalCliente = lazy(() => import("@/pages/PortalCliente"));

// Bairros
const Bairros = lazy(() => import("@/pages/Bairros"));

// Cidades
const Cidades = lazy(() => import("@/pages/Cidades"));

// Assinatura Digital (pública)
const AssinaturaDigital = lazy(() => import("@/pages/AssinaturaDigital"));
const PropostaPublica = lazy(() => import("@/pages/PropostaPublica"));

// Blog Admin
const AdminBlog = lazy(() => import("@/pages/admin/Blog"));
const AdminBlogEditor = lazy(() => import("@/pages/admin/BlogEditor"));

// Área do Proprietário de Imóvel
const ProprietarioLogin = lazy(() => import("@/pages/ProprietarioLogin"));
const ProprietarioDashboard = lazy(() => import("@/pages/ProprietarioDashboard"));
const CalculadoraFinanciamento = lazy(() => import("@/pages/CalculadoraFinanciamento"));

// Site público - DESATIVADO (apenas landing pages e mini sites mantidos)
// const SiteLayout = lazy(() => import("@/components/site/SiteLayout"));
// const SiteHome = lazy(() => import("@/pages/site/SiteHome"));
// const SiteImoveis = lazy(() => import("@/pages/site/SiteImoveis"));
// const SiteImovelDetalhes = lazy(() => import("@/pages/site/SiteImovelDetalhes"));
// const SiteEmpreendimentos = lazy(() => import("@/pages/site/SiteEmpreendimentos"));
// const SiteEmpreendimentoDetalhes = lazy(() => import("@/pages/site/SiteEmpreendimentoDetalhes"));
// const SiteConstrutoras = lazy(() => import("@/pages/site/SiteConstrutoras"));
// const SiteConstruturaDetalhes = lazy(() => import("@/pages/site/SiteConstruturaDetalhes"));
// const SiteContato = lazy(() => import("@/pages/site/SiteContato"));
// const SiteInstitucional = lazy(() => import("@/pages/site/SiteInstitucional"));
// const SiteBlog = lazy(() => import("@/pages/site/SiteBlog"));
// const SiteBlogPost = lazy(() => import("@/pages/site/SiteBlogPost"));
// const SiteFavoritos = lazy(() => import("@/pages/site/SiteFavoritos"));
// const SiteCadastro = lazy(() => import("@/pages/site/SiteCadastro"));
// const SiteAnunciarImovel = lazy(() => import("@/pages/site/SiteAnunciarImovel"));
// const SiteBairros = lazy(() => import("@/pages/site/SiteBairros"));
// const SiteBairroDetalhes = lazy(() => import("@/pages/site/SiteBairroDetalhes"));
const MiniSitePublic = lazy(() => import("@/pages/site/MiniSitePublic"));
const LandingPagePublic = lazy(() => import("@/pages/site/LandingPagePublic"));
// const PublicSite = lazy(() => import("@/pages/PublicSite"));

// Componente de rota protegida
const ProtectedRoute = ({ children, isAuth, redirectTo = "/login" }) => {
  return isAuth ? children : <Navigate to={redirectTo} />;
};

// Componente de rota lazy protegida
const LazyProtectedRoute = ({ component: Component, isAuth, redirectTo = "/login", ...props }) => {
  if (!isAuth) return <Navigate to={redirectTo} />;
  return (
    <Suspense fallback={<PageLoader />}>
      <Component {...props} />
    </Suspense>
  );
};

// Função para obter a home page baseada na role do usuário
const getDefaultHomePage = () => {
  const user = JSON.parse(localStorage.getItem('user'));
  if (!user) return "/crm/login";
  if (user.role === "corretor") return "/crm/funil";
  return "/crm/dashboard";
};

// Componente de proteção do Dashboard (apenas proprietário/admin)
const DashboardProtectedRoute = ({ children, isAuth }) => {
  if (!isAuth) return <Navigate to="/crm/login" />;
  const user = JSON.parse(localStorage.getItem('user'));
  if (user?.role === "corretor") {
    return <Navigate to="/crm/clientes" />;
  }
  return children;
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!localStorage.getItem('token'));
  const [isProprietarioAuth, setIsProprietarioAuth] = useState(() => !!localStorage.getItem('proprietario_token'));

  const handleLogin = () => setIsAuthenticated(true);
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    window.location.href = '/crm/login';
  };
  const handleProprietarioLogin = () => setIsProprietarioAuth(true);
  const handleProprietarioLogout = () => {
    localStorage.removeItem('proprietario_token');
    localStorage.removeItem('proprietario');
    setIsProprietarioAuth(false);
    window.location.href = '/crm/proprietario/login';
  };

  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <ScrollToTop />
          <Routes>
            {/* ========================================= */}
            {/* SITE PÚBLICO INSTITUCIONAL - REMOVIDO    */}
            {/* Todas as rotas públicas redirecionam para */}
            {/* o CRM. Apenas o CRM permanece ativo.      */}
            {/* ========================================= */}
            <Route path="/" element={<Navigate to="/crm/login" replace />} />
            <Route path="/optima" element={<Navigate to="/crm/login" replace />} />
            <Route path="/site" element={<Navigate to="/crm/login" replace />} />
            <Route path="/sobre" element={<Navigate to="/crm/login" replace />} />
            <Route path="/contato" element={<Navigate to="/crm/login" replace />} />
            <Route path="/obrigado" element={<Navigate to="/crm/login" replace />} />
            <Route path="/trabalhe-conosco" element={<Navigate to="/crm/login" replace />} />
            <Route path="/empreendimentos" element={<Navigate to="/crm/login" replace />} />
            <Route path="/empreendimento/:slug" element={<Navigate to="/crm/login" replace />} />
            <Route path="/empreendimentos/:slug" element={<Navigate to="/crm/login" replace />} />
            <Route path="/litoral" element={<Navigate to="/crm/login" replace />} />
            <Route path="/blog" element={<Navigate to="/crm/login" replace />} />
            <Route path="/blog/:slug" element={<Navigate to="/crm/login" replace />} />
            
            {/* ========================================= */}
            {/* CONTEXTO CRM (/crm/*) - Central de Lançamentos */}
            {/* ========================================= */}
            
            {/* Login CRM */}
            <Route 
              path="/crm/login" 
              element={isAuthenticated ? <Navigate to={getDefaultHomePage()} /> : <Login onLogin={handleLogin} />} 
            />
            
            {/* Dashboard CRM (apenas proprietário/admin) */}
            <Route 
              path="/crm/dashboard" 
              element={
                <DashboardProtectedRoute isAuth={isAuthenticated}>
                  <Dashboard onLogout={handleLogout} />
                </DashboardProtectedRoute>
              } 
            />
            
            {/* Rotas do CRM (lazy) */}
            <Route path="/crm/funil" element={<LazyProtectedRoute component={FunilVendas} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/unidades" element={<LazyProtectedRoute component={Tabelas} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/tabelas" element={<Navigate to="/crm/unidades" replace />} />
            <Route path="/crm/tabelas-pagamento" element={<Navigate to="/crm/unidades" replace />} />
            <Route path="/crm/tabelas-personalizadas" element={<Navigate to="/crm/unidades" replace />} />
            
            {/* Índices Econômicos */}
            <Route path="/crm/indices/cub" element={<LazyProtectedRoute component={CubHistorico} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/indices/igpm" element={<LazyProtectedRoute component={Igpm} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/indices/incc" element={<LazyProtectedRoute component={Incc} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/indices/ipca" element={<LazyProtectedRoute component={Ipca} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/cub" element={<Navigate to="/crm/indices/cub" replace />} />
            <Route path="/crm/temas" element={<Navigate to="/crm/site-config" replace />} />
            <Route path="/crm/unidade/:id" element={<LazyProtectedRoute component={UnidadePerfil} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/propostas" element={<LazyProtectedRoute component={PropostasPage} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/proposta/:id" element={<LazyProtectedRoute component={PropostaPerfil} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/calculadora-financiamento" element={<LazyProtectedRoute component={CalculadoraFinanciamento} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/clients" element={<LazyProtectedRoute component={Clients} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/properties" element={<LazyProtectedRoute component={Properties} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/profile" element={<LazyProtectedRoute component={Profile} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/action-logs" element={<LazyProtectedRoute component={ActionLogs} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/ajuda" element={<LazyProtectedRoute component={Ajuda} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/captacoes" element={<LazyProtectedRoute component={Captacoes} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/empreendimentos" element={<LazyProtectedRoute component={Empreendimentos} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/empreendimentos/:id" element={<LazyProtectedRoute component={EmpreendimentoDetalhes} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/clientes/:clientId" element={<LazyProtectedRoute component={ClienteDetalhes} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/captacoes/:captacaoId" element={<LazyProtectedRoute component={CaptacaoDetalhes} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/construtoras" element={<LazyProtectedRoute component={Construtoras} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/proprietarios" element={<LazyProtectedRoute component={Proprietarios} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/agendamentos" element={<LazyProtectedRoute component={Agendamentos} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/disputas" element={<LazyProtectedRoute component={Disputas} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/equipe" element={<LazyProtectedRoute component={Equipe} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/cargos-permissoes" element={<LazyProtectedRoute component={RBACManager} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/configuracoes" element={<LazyProtectedRoute component={Configuracoes} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/mini-site" element={<LazyProtectedRoute component={MiniSite} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/landing-pages" element={<LazyProtectedRoute component={LandingPages} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/landing-pages/editor" element={<LazyProtectedRoute component={LandingPageEditor} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/landing-pages/editor/:id" element={<LazyProtectedRoute component={LandingPageEditor} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/visitantes" element={<LazyProtectedRoute component={Visitantes} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/site-config" element={<LazyProtectedRoute component={SiteConfig} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/bairros" element={<LazyProtectedRoute component={Bairros} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/cidades" element={<LazyProtectedRoute component={Cidades} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/scrap-ia" element={<LazyProtectedRoute component={Importacao} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/lembretes" element={<LazyProtectedRoute component={Lembretes} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/email-marketing" element={<LazyProtectedRoute component={EmailMarketing} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/propostas" element={<LazyProtectedRoute component={Propostas} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/compradores" element={<LazyProtectedRoute component={Compradores} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/compradores/:id" element={<LazyProtectedRoute component={CompradorDetalhes} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/contratos" element={<LazyProtectedRoute component={Contratos} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/pagamentos" element={<LazyProtectedRoute component={Pagamentos} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/repasse-bancario" element={<LazyProtectedRoute component={RepasseBancario} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/reservas" element={<LazyProtectedRoute component={Reservas} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/rede-corretores" element={<LazyProtectedRoute component={RedeCorretores} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/rede-corretores/funil" element={<LazyProtectedRoute component={FunilCorretores} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/rede-corretores/:tab" element={<LazyProtectedRoute component={RedeCorretores} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/rede-corretores/corretor/:id" element={<LazyProtectedRoute component={CorretorPerfil} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/atualizacao" element={<LazyProtectedRoute component={Atualizacao} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            
            {/* Bolsão de Leads - CRM */}
            <Route path="/crm/bolsao" element={<LazyProtectedRoute component={Bolsao} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/bolsao/campanhas" element={<LazyProtectedRoute component={CampanhasBolsao} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/bolsao/campanhas/:campanhaId" element={<LazyProtectedRoute component={CampanhaDetalhes} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/bolsao/minha-campanha" element={<LazyProtectedRoute component={MinhaCampanha} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            
            {/* Pixel Pro - CRM */}
            <Route path="/crm/pixel-pro" element={<LazyProtectedRoute component={PixelProDashboard} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/pixel-pro/events" element={<LazyProtectedRoute component={PixelProEvents} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/pixel-pro/rules" element={<LazyProtectedRoute component={PixelProRules} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/pixel-pro/config" element={<LazyProtectedRoute component={PixelProConfig} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/pixel-pro/whatsapp" element={<LazyProtectedRoute component={PixelProWhatsApp} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/pixel-pro/meta-capi" element={<LazyProtectedRoute component={MetaCAPIConfig} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            
            {/* Central ADS - Gestão de Anúncios */}
            <Route path="/crm/solk-ads" element={<LazyProtectedRoute component={SolkAdsDashboard} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/solk-ads/campanhas" element={<LazyProtectedRoute component={CampanhasWhatsApp} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            
            {/* Academy - CRM */}
            <Route path="/crm/academy" element={<LazyProtectedRoute component={Academy} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/academy/curso/:cursoId" element={<LazyProtectedRoute component={AcademyCurso} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/academy/aula/:aulaId" element={<LazyProtectedRoute component={AcademyAula} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/academy/questionario/:questionarioId" element={<LazyProtectedRoute component={AcademyQuestionario} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/academy/admin" element={<LazyProtectedRoute component={AcademyAdmin} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            
            {/* Secretária IA - CRM */}
            <Route path="/crm/secretaria" element={<LazyProtectedRoute component={SecretariaIA} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            
            {/* Blog Admin - CRM */}
            <Route path="/crm/admin/blog" element={<LazyProtectedRoute component={AdminBlog} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/admin/blog/novo" element={<LazyProtectedRoute component={AdminBlogEditor} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/crm/admin/blog/:id" element={<LazyProtectedRoute component={AdminBlogEditor} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            
            {/* Blog Admin - Rotas alternativas sem /crm */}
            <Route path="/admin/blog" element={<LazyProtectedRoute component={AdminBlog} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/admin/blog/novo" element={<LazyProtectedRoute component={AdminBlogEditor} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            <Route path="/admin/blog/:id" element={<LazyProtectedRoute component={AdminBlogEditor} isAuth={isAuthenticated} onLogout={handleLogout} />} />
            
            {/* Proprietário de Imóvel - CRM */}
            <Route 
              path="/crm/proprietario/login" 
              element={isProprietarioAuth ? <Navigate to="/crm/proprietario/dashboard" /> : (
                <Suspense fallback={<PageLoader />}>
                  <ProprietarioLogin onLogin={handleProprietarioLogin} />
                </Suspense>
              )} 
            />
            <Route 
              path="/crm/proprietario/dashboard" 
              element={
                <LazyProtectedRoute 
                  component={ProprietarioDashboard} 
                  isAuth={isProprietarioAuth} 
                  redirectTo="/crm/proprietario/login"
                  onLogout={handleProprietarioLogout} 
                />
              } 
            />
            
            {/* ========================================= */}
            {/* Redirects de rotas antigas do site */}
            {/* ========================================= */}
            
            <Route path="/imoveis" element={<Navigate to="/crm/login" replace />} />
            <Route path="/imovel/*" element={<Navigate to="/crm/login" replace />} />
            <Route path="/construtoras" element={<Navigate to="/crm/login" replace />} />
            <Route path="/construtora/*" element={<Navigate to="/crm/login" replace />} />
            <Route path="/bairros" element={<Navigate to="/crm/login" replace />} />
            <Route path="/bairro/*" element={<Navigate to="/crm/login" replace />} />
            <Route path="/institucional" element={<Navigate to="/crm/login" replace />} />
            <Route path="/favoritos" element={<Navigate to="/crm/login" replace />} />
            <Route path="/cadastro" element={<Navigate to="/crm/login" replace />} />
            <Route path="/anunciar-imovel" element={<Navigate to="/crm/login" replace />} />
            
            {/* ========================================= */}
            {/* ROTAS PÚBLICAS (sem prefixo de contexto) */}
            {/* ========================================= */}
            
            {/* Mini Site e Landing Pages Públicas */}
            <Route path="/s/:slug" element={<Suspense fallback={<PageLoader />}><MiniSitePublic /></Suspense>} />
            <Route path="/lp/:slug" element={<Suspense fallback={<PageLoader />}><LandingPagePublic /></Suspense>} />
            
            {/* Landing Page WhatsApp - Pública */}
            <Route path="/l/:tenantSlug/:slug" element={<Suspense fallback={<PageLoader />}><LandingWhatsApp /></Suspense>} />
            
            {/* Assinatura Digital - Pública */}
            <Route path="/assinatura/:propostaId" element={<Suspense fallback={<PageLoader />}><AssinaturaDigital /></Suspense>} />
            
            {/* Visualização de Proposta - Pública */}
            <Route path="/proposta/:propostaId" element={<Suspense fallback={<PageLoader />}><PropostaPublica /></Suspense>} />
            
            {/* Portal do Cliente - Pública */}
            <Route path="/portal-cliente/:token" element={<Suspense fallback={<PageLoader />}><PortalCliente /></Suspense>} />
            
            {/* Redirect padrão - CRM */}
            <Route path="/crm" element={<Navigate to={isAuthenticated ? getDefaultHomePage() : "/crm/login"} />} />
            
            {/* Redirect /login para /crm/login */}
            <Route path="/login" element={<Navigate to="/crm/login" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </div>
    </HelmetProvider>
  );
}

export default App;
